import React from 'react';

import { di } from 'react-magnetic-di';

import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { SentryErrorBoundary, type SentryErrorBoundaryProps } from '../sentry';

type PageSegmentProps = {
	children: React.ReactNode;
	fallbackComponent?: SentryErrorBoundaryProps['fallbackComponent'];
	name: string;
	/**
	 * Optional predicate that decides whether an error caught by the segment
	 * should be reported to UFO via `useUFOReportError`.
	 *
	 * Defaults to `() => true` (always report). Set this to skip reporting for
	 * errors that should not impact UFO reliability metrics for this segment,
	 * e.g. expected 403s on permission-gated queries.
	 *
	 * Sentry logging via `SentryErrorBoundary` is always invoked regardless of
	 * this predicate — only the UFO interaction error is gated.
	 */
	shouldReportError?: (error: Error) => boolean;
	teamName?: SentryErrorBoundaryProps['teamName'];
	touchpointId?: SentryErrorBoundaryProps['touchpointId'];
};

const DefaultFallbackComponent = () => null;

const defaultShouldReportError = () => true;

const PageSegmentInternal = ({
	name: segmentName,
	children,
	fallbackComponent = DefaultFallbackComponent,
	shouldReportError = defaultShouldReportError,
	teamName,
	touchpointId,
}: PageSegmentProps) => {
	di(useUFOReportError);
	const reportError = useUFOReportError();
	const errorName = `PageSegmentError:${segmentName}`;

	return (
		<SentryErrorBoundary
			loggerName={errorName}
			fallbackComponent={fallbackComponent}
			teamName={teamName}
			touchpointId={touchpointId}
			onError={(error) => {
				if (!shouldReportError(error)) {
					return;
				}

				// using `useUFOReportError` instead of `setInteractionError`
				// because `setInteractionError` will not work due to mismatching activeInteraction
				// https://atlassian.slack.com/archives/C095EAEKV63/p1756710099202429?thread_ts=1756193218.083929&cid=C095EAEKV63
				reportError({
					name: errorName,
					errorMessage: error.message,
					errorStack: error.stack,
				});
			}}
		>
			{children}
		</SentryErrorBoundary>
	);
};

/**
 * @param name - The name of the segment. Used for error handling and UFOSegment name.
 * @param children - The children of the segment.
 * @param fallbackComponent - The component to render when an error occurs.
 * @returns A component that wraps a segment of the page and provides error handling.
 */
export const PageSegment = (props: PageSegmentProps): React.JSX.Element => {
	return (
		<UFOSegment name={props.name}>
			<PageSegmentInternal {...props} />
		</UFOSegment>
	);
};
